"use client";

import { Lookup, SpringRef } from "@react-spring/web";
import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import ActionButton from "@/components/molecules/action-button/action-button";
import BackToRenewal from "@/components/molecules/renewals/back-to-renewal/back-to-renewal";
import OrganizerEditForm from "@/components/molecules/renewals/organizer/organizer-edit-form";
import { QuestionSeven } from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { PRODUCT } from "@/constants";
import { useAppDispatch } from "@/store/hook";
import { RootState } from "@/store/root-reducer";
import { updateQuoteOnQuestion } from "@/store/slices/quote/quote.action";
import { Organiser } from "@/types/Organiser";
import { QuotePayload } from "@/types/quote/quote-data";
import { OrganiserContent } from "@/utils/schema/organiser-page-schema.utils";

import UnSavedChangesWarningMessage from "../../un-saved-changes-warning-message/un-saved-changes-warning-message";

type CommonPageContent = OrganiserContent & { page: "organiser" };

export default function OrganizerEdit({
  pageContent,
  formRef,
  ctaRef,
}: Readonly<{
  pageContent: CommonPageContent;
  formRef?: SpringRef<Lookup>;
  ctaRef?: SpringRef<Lookup>;
}>) {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const quoteResponse = useSelector((state: RootState) => state.quote);

  const [organizerDetails, setOrganizerDetails] = useState<
    Organiser | undefined
  >(undefined);

  const [updatedOrganizerData, setUpdatedOrganizerData] = useState<
    Organiser | undefined
  >(undefined);

  const [isUpdateCompleted, setIsUpdateCompleted] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);

  useEffect(() => {
    if (quoteResponse?.attributes?.organiser) {
      setOrganizerDetails(quoteResponse.attributes.organiser);
    }
  }, [quoteResponse]);

  const handleUpdatedData = (formData: Organiser, isCompleted: boolean) => {
    setUpdatedOrganizerData(formData);
    setIsUpdateCompleted(isCompleted);
  };

  const handleUnSavedWarningMessageContinueBtn = () => {
    if (quoteResponse?.attributes?.organiser) {
      setOrganizerDetails(quoteResponse.attributes.organiser);
    }
    setIsOpenUnsavedChangesModal(false);
    router.back();
  };

  const submitUpdatedOrganizerData = async () => {
    if (updatedOrganizerData && isUpdateCompleted && !isFormSubmitted) {
      try {
        const payload = {
          ...quoteResponse,
          attributes: {
            ...quoteResponse?.attributes,
            organiser: updatedOrganizerData,
          },
        };
        setIsFormSubmitted(true);
        const result = await dispatch(
          updateQuoteOnQuestion({
            payload: {
              ...payload,
              viewPage: Routes.Organiser,
            } as unknown as QuotePayload,
            dispatch,
            router,
          })
        );
        const originalPromiseResult = unwrapResult(result);
        if (originalPromiseResult) {
          router.push(`/`);
        } else {
          setIsFormSubmitted(false);
        }
      } catch (error) {
        setIsFormSubmitted(false);
        // eslint-disable-next-line no-console
        console.error("Error updating organiser data:", error);
      }
    }
  };

  const renderEditForm = () => {
    return (
      <div data-testid="organizer-edit-content">
        {pageContent.backToRenewalsText && (
          <div>
            <BackToRenewal
              label={pageContent.backToRenewalsText}
              className="pb-[0px] 
        top-[-2px] !px-[0px] mb-spacing-l md:!mb-spacing-l lg:mx-[0px]"
              onClick={() => setIsOpenUnsavedChangesModal(true)}
            />
          </div>
        )}

        <h1
          className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
          md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center md:px-0 px-spacing-m"
        >
          {pageContent?.questionText}
        </h1>
        {organizerDetails && (
          <OrganizerEditForm
            usagePage={QuestionSeven}
            inputOnePlaceholder={pageContent?.inputOnePlaceholder}
            inputTwoPlaceholder={pageContent?.inputTwoPlaceholder}
            dobPlaceholder={pageContent?.dobPlaceholder}
            handleUpdatedData={handleUpdatedData}
            addressLineOne={pageContent?.addressLineOne}
            addressLineTwo={pageContent?.addressLineTwo}
            organizerDetails={organizerDetails}
            townCityPlaceholder={pageContent?.TownCityPlaceholder}
            postcodePlaceholder={pageContent?.postcodePlaceholder}
            addressLineOneErrorMasg={pageContent?.addressLineOneErrorMasg}
            addressLineTwoErrorMasg={pageContent?.addressLineTwoErrorMasg}
            adressInputPlaceholder={pageContent?.adressInputPlaceholder}
            TownCityErrorMasg={pageContent?.TownCityErrorMasg}
            postcodeErrorMasg={pageContent?.postcodeErrorMasg}
            searchByPostCodeButton={pageContent?.searchByPostCodeButton}
            enterAddressButton={pageContent?.enterAddressButton}
            renewalInfoText={pageContent?.renewalNonEditInfoBox}
            brand={PRODUCT as "staysure" | "avanti" | undefined}
          />
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="question-organiser-test-id"
      className={`rounded-radius-xxl 
        bg-surface-neutral-option-1 rounded-b-radius-xxl  
        md:pt-spacing-xxl md:pb-spacing-xxl pt-spacing-m pb-0 px-spacing-m h-full`}
    >
      <div className="lg:w-[414px] md:w-[400px] w-full mx-auto relative h-full flex flex-col justify-between">
        <UnSavedChangesWarningMessage
          isOpen={isOpenUnsavedChangesModal}
          onContinueClick={handleUnSavedWarningMessageContinueBtn}
          setIsOpen={setIsOpenUnsavedChangesModal}
        />
        <TransformAnimation animationRef={formRef} currentPage={QuestionSeven}>
          {renderEditForm()}
        </TransformAnimation>
        <FadeAnimation animationRef={ctaRef}>
          <ActionButton
            page={QuestionSeven}
            ctaButtonText={pageContent?.finishEditingBtnText}
            isLoading={isFormSubmitted}
            submitButtonClick={submitUpdatedOrganizerData}
            backButtonClick={() => {
              setIsOpenUnsavedChangesModal(true);
            }}
            showBackBtn
          />
        </FadeAnimation>
      </div>
    </div>
  );
}
